import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Zap, Building2, Bell, CheckCircle2, User, Bot, Video, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card } from "./ui/card";
import "../pages/AIInterview.css";

const FEATURES = [
  {
    icon: Mic,
    title: "Real Interview Simulation",
    desc: "Talk to an AI interviewer that adapts in real-time to your answers.",
    accent: "#E8940F",
  },
  {
    icon: Zap,
    title: "Instant AI Feedback",
    desc: "Get scored on accuracy, confidence, and code quality after every answer.",
    accent: "#059669",
  },
  {
    icon: Building2,
    title: "Company-Specific Questions",
    desc: "Curated question banks from Google, Amazon, Microsoft and more.",
    accent: "#059669",
  },
];

const GridLines = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[20, 40, 60, 80].map((pct) => (
      <motion.div
        key={pct}
        className="absolute left-0 right-0 h-px bg-accent/5"
        style={{ top: `${pct}%` }}
        animate={{ opacity: [0.3, 0.8, 0.3] }}
        transition={{ duration: 4 + pct / 20, repeat: Infinity, delay: pct / 40 }}
      />
    ))}
    {[15, 30, 50, 70, 85].map((pct) => (
      <motion.div
        key={pct}
        className="absolute top-0 bottom-0 w-px bg-accent/4"
        style={{ left: `${pct}%` }}
        animate={{ opacity: [0.2, 0.6, 0.2] }}
        transition={{ duration: 5 + pct / 30, repeat: Infinity, delay: pct / 60 }}
      />
    ))}
    <motion.div
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] bg-accent/5 rounded-full blur-[120px]"
      animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.8, 0.4] }}
      transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
    />
  </div>
);

const AIInterviewTeaser = () => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitted(true);
  };

  return (
    <section className="theme-light section-base bg-background relative py-28 md:py-36 overflow-hidden border-t border-border">
      <GridLines />

      {/* Dot texture */}
      <div
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle, #F59E0B 1px, transparent 1px)",
          backgroundSize: "36px 36px",
        }}
      />

      <div className="relative z-10 max-w-6xl mx-auto px-6 flex flex-col items-center text-center">

        {/* ── Launching Soon badge ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative mb-12"
        >
          <div className="border-border bg-card relative inline-flex items-center gap-2.5 px-6 py-2 border">
            <motion.div
              animate={{ scale: [1, 1.3, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-2.5 h-2.5 bg-primary"
            />
            <span className="text-[10px] font-semibold text-foreground uppercase tracking-[0.3em]">
              Launching Soon
            </span>
          </div>
        </motion.div>

        {/* ── Headline ── */}
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-3xl md:text-5xl font-semibold text-foreground leading-[1.1] tracking-tighter mb-8 uppercase"
        >
          Ace Your Python <br />
          <span className="text-primary italic">Interview</span> with AI
        </motion.h2>

        {/* ── Subheadline ── */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="font-sora text-muted-foreground text-lg leading-relaxed max-w-xl mb-20"
        >
          Mock interviews, instant feedback, and company-specific question banks —
          powered by AI.
        </motion.p>

        {/* ── Interactive Showcase ── */}
        <div className="w-full text-left mb-20">
           {/* Overlapping grid for Mockup and Feedback */}
           <div className="grid lg:grid-cols-12 gap-8 relative">
              {/* Setup / Chat Mockup (Left/Center) */}
              <div className="lg:col-span-7 bg-card border-border border p-8 md:p-12 shadow-2xl relative atm-card text-left">
                <div className="pulsing-dot" />
                <div className="bg-primary text-background absolute -top-4 -right-4 px-4 py-1.5 font-semibold text-[9px] uppercase tracking-widest relative z-20 shadow-lg">Live Demo</div>
                
                <div className="space-y-8 relative z-10">
                  {/* Setup Snippet */}
                  <div className="flex flex-col sm:flex-row gap-6 mb-10 pb-10 border-b border-border/50">
                    <div className="flex-1">
                        <label className="text-[9px] font-semibold uppercase tracking-[0.3em] text-muted-foreground mb-3 block">01 / Target Role</label>
                        <div className="bg-background border-border h-12 border flex items-center px-4 text-[10px] font-semibold text-foreground uppercase">Senior AI Engineer</div>
                    </div>
                    <div className="flex-1">
                        <label className="text-[9px] font-semibold uppercase tracking-[0.3em] text-muted-foreground mb-3 block">02 / Logic Intensity</label>
                        <div className="flex gap-2">
                            {['Basic', 'Med', 'Hard'].map(d => (
                              <div key={d} className={`flex-1 h-12 flex items-center justify-center text-[9px] font-semibold uppercase border transition-all ${d === 'Hard' ? 'bg-accent border-accent text-background shadow-[0_0_15px_rgba(245,158,11,0.2)]' : 'border-border text-muted-foreground'}`}>{d}</div>
                            ))}
                        </div>
                    </div>
                  </div>

                  {/* Video Feed Mockup (Split View) */}
                  <div className="video-panel mb-8">
                    <div className="pane human">
                      <div className="pane-label"><span className="sq human"></span>YOU</div>
                      <div className="detect-frame">
                        <span className="detect-corner tl"></span>
                        <span className="detect-corner tr"></span>
                        <span className="detect-corner bl"></span>
                        <span className="detect-corner br"></span>
                      </div>
                      <div className="human-avatar">
                        <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.5">
                          <circle cx="12" cy="8" r="4"/>
                          <path d="M4 20c0-4 3.5-7 8-7s8 3 8 7"/>
                        </svg>
                      </div>
                      <div className="metrics-row">
                        <div className="metric-chip">
                          <span className="m-icon">👁</span>
                          <div className="m-text">
                            <span className="m-label">Eye Contact</span>
                            <span className="m-value"><span className="dot"></span>Focused</span>
                          </div>
                        </div>
                        <div className="metric-chip">
                          <span className="m-icon">🎵</span>
                          <div className="m-text">
                            <span className="m-label">Tone</span>
                            <span className="m-value"><span className="dot"></span>Confident</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="pane system">
                      <div className="pane-label"><span className="sq system"></span>AI INTERVIEWER</div>
                      <div className="ai-core">
                        <svg viewBox="0 0 24 24" fill="none" stroke="#F5A623" strokeWidth="1.5">
                          <rect x="5" y="7" width="14" height="11" rx="3"/>
                          <path d="M9 7V5a3 3 0 0 1 6 0v2"/>
                          <circle cx="9.5" cy="12.5" r="1"/>
                          <circle cx="14.5" cy="12.5" r="1"/>
                        </svg>
                      </div>
                      <div className="ai-status"><span className="pdot"></span>Speaking</div>
                      <div className="waveform">
                        <div className="wbar" style={{height:'8px'}}></div>
                        <div className="wbar" style={{height:'16px'}}></div>
                        <div className="wbar" style={{height:'10px'}}></div>
                        <div className="wbar" style={{height:'20px'}}></div>
                        <div className="wbar" style={{height:'12px'}}></div>
                        <div className="wbar" style={{height:'18px'}}></div>
                        <div className="wbar" style={{height:'9px'}}></div>
                      </div>
                    </div>

                    <div className="divider-line"></div>

                    <div className="status-cluster">
                      <div className="live-badge"><span className="live-dot"></span>LIVE</div>
                      <div className="timer">04:12</div>
                    </div>
                  </div>

                  {/* Chat Mockup */}
                  <div className="space-y-6">
                    <div className="bg-background border-border border p-6 relative max-w-[90%] self-start atm-card text-left">
                      <div className="border-border border-l-transparent border-t-transparent bg-background absolute -left-2 top-6 w-2 h-2 rotate-[-45deg] border-b border-r" />
                      <div className="flex items-center gap-3 mb-3">
                        <Bot className="w-4 h-4 text-primary" />
                        <span className="text-[9px] font-semibold text-primary uppercase tracking-[0.3em]">AI Interviewer</span>
                      </div>
                      <p className="font-sora text-sm text-foreground leading-relaxed">"Based on your resume, you've worked extensively with Python concurrency. Can you explain the difference between `asyncio` and `threading` in the context of I/O bound tasks?"</p>
                    </div>

                    <div className="bg-primary/5 border-primary/20 border p-6 relative max-w-[90%] ml-auto atm-card text-left">
                      <div className="border-primary/20 border-r-transparent border-t-transparent bg-primary/5 absolute -right-2 top-6 w-2 h-2 rotate-[45deg] border-b border-l" />
                      <div className="flex items-center gap-3 mb-3 justify-end">
                        <span className="text-[9px] font-semibold text-muted-foreground uppercase tracking-[0.3em]">Your Response</span>
                        <Video className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <p className="font-sora text-sm text-foreground leading-relaxed text-right italic">"Threading uses multiple OS threads while asyncio uses a single-threaded event loop..."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Feedback Report (Right overlapping) */}
              <div className="lg:col-span-5 bg-card border-border border p-8 md:p-12 shadow-2xl simulator-panel atm-card lg:-ml-12 lg:mt-32 relative z-20 text-left">
                <div className="pulsing-dot" />
                <div className="flex justify-between items-start mb-10 relative z-20">
                  <div>
                    <h3 className="text-[9px] font-semibold text-primary uppercase tracking-[0.5em] mb-3">Execution Report</h3>
                    <h2 className="text-2xl font-semibold uppercase tracking-tighter leading-none">FEEDBACK <br/><span className="text-primary italic">MATRIX</span></h2>
                  </div>
                  <div className="bg-background border-border text-center border p-4 w-24 relative group overflow-hidden">
                    <div className="text-4xl font-semibold text-accent relative z-10">84</div>
                    <div className="text-[8px] font-semibold text-muted-foreground uppercase tracking-widest mt-2 relative z-10">Score</div>
                  </div>
                </div>

                <div className="space-y-8 relative z-20 mb-10">
                  {[
                    { label: "Technical Skills", score: 92 },
                    { label: "Communication", score: 78 },
                    { label: "Problem Solving", score: 85 }
                  ].map((s, i) => (
                    <div key={i}>
                      <div className="flex justify-between text-[9px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-3">
                        <span>{s.label}</span>
                        <span className="text-foreground">{s.score}%</span>
                      </div>
                      <div className="bg-background h-1.5 overflow-hidden relative">
                         <div className="absolute inset-0 bg-dot-grid opacity-[0.1]" />
                        <motion.div 
                          initial={{ width: 0 }}
                          whileInView={{ width: `${s.score}%` }}
                          viewport={{ once: true }}
                          transition={{ duration: 1.5, ease: "circOut" }}
                          className="h-full bg-primary relative z-10"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="relative z-20 pt-8 border-t border-border/50">
                  <h4 className="text-[9px] font-semibold uppercase tracking-[0.3em] text-accent mb-6 flex items-center gap-3">
                    <Sparkles className="w-4 h-4" />
                    SUGGESTIONS FOR YOU
                  </h4>
                  <ul className="space-y-6">
                    {[
                      "Mentioned Python Global Interpreter Lock (GIL) but didn't explain its impact on multi-threading architectures.",
                      "Excellent explanation of generator expressions; consider mentioning memory efficiency in larger production datasets."
                    ].map((s, i) => (
                      <li key={i} className="flex gap-4 items-start text-sm text-muted-foreground leading-relaxed group">
                        <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
                        <span className="font-sora text-xs group-hover:text-foreground transition-colors">{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
           </div>
        </div>

        {/* ── Divider ── */}
        <div className="w-full h-px bg-gradient-to-r from-transparent via-[#F59E0B]/20 to-transparent mb-20" />

        {/* ── Email capture ── */}
        <div className="w-full max-w-lg">
          {submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center gap-4"
            >
              <div className="bg-primary-subtle border-primary-subtle w-14 h-14 border rounded-lg flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-primary" />
              </div>
              <p className="text-sm font-semibold text-primary uppercase tracking-widest">
                You're on the list!
              </p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-0 w-full">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ENTER EMAIL ADDRESS"
                required
                className="flex-1 h-16"
              />
              <Button
                type="submit"
                variant="default"
                className="h-16 px-10 flex-shrink-0"
              >
                <Bell className="w-4 h-4 mr-3" />
                Notify Me
              </Button>
            </form>
          )}
          {!submitted && (
            <p className="text-muted-foreground text-[9px] uppercase tracking-[0.3em] mt-8 font-semibold">
              Be the first to know. Zero Spam. Real updates.
            </p>
          )}
        </div>

        {/* ── Bottom stat strip ── */}
        <div className="mt-24 flex flex-wrap items-center justify-center gap-10 text-muted-foreground text-[10px] font-semibold uppercase tracking-widest">
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 bg-accent" />
            Limited spots available
          </span>
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 bg-primary" />
            Q3 2025 launch
          </span>
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 bg-foreground" />
            Early access free
          </span>
        </div>

      </div>
    </section>
  );
};

export default AIInterviewTeaser;
